import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toApiMetricFilter,
  toExperienceMetricPoint,
} from "../../../../domain/mappers/metricsMappers";
import {
  Dimension,
  ExperienceMetricPoint,
} from "../../../../domain/models/metrics";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";
import { normalizeMetricDateRange } from "../../utils/metricDateRange";
import type { ExperienceUsageData } from "../utils/usageUtils";

export const EXPERIENCE_DATA_METRICS_QUERY_KEY = "experienceDataMetrics";

interface UseExperienceDataMetricsParams {
  startDate: Date;
  endDate: Date;
  dimension: Dimension;
  contentMetadataIds?: string[];
  countries?: string[];
  minSessionDurationInMinutes?: number;
  enabled?: boolean;
}

interface UseExperienceDataMetricsResult {
  data: ExperienceUsageData[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

const toExperienceUsageData = (
  point: ExperienceMetricPoint,
): ExperienceUsageData => ({
  id: point.experienceId,
  name: point.experienceName,
  hoursInVr: point.hoursInVr,
  userSessions: point.userSessions,
  uniqueActiveUsers: point.uniqueUsers,
  certifications: point.certifications,
});

export const useExperienceDataMetrics = ({
  startDate,
  endDate,
  dimension,
  contentMetadataIds,
  countries,
  minSessionDurationInMinutes,
  enabled = true,
}: UseExperienceDataMetricsParams): UseExperienceDataMetricsResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const normalizedRange = useMemo(
    () => normalizeMetricDateRange(startDate, endDate),
    [startDate, endDate],
  );

  const { startDate: normalizedStartDate, endDate: normalizedEndDate } =
    normalizedRange;

  const { data, error, isLoading, isFetching } = useQuery<
    ExperienceUsageData[]
  >({
    queryKey: [
      EXPERIENCE_DATA_METRICS_QUERY_KEY,
      companyId,
      normalizedStartDate.toISOString(),
      normalizedEndDate.toISOString(),
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const filter = toApiMetricFilter({
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        dimension,
        contentMetadataIds,
        countries,
        minSessionDurationInMinutes,
      });

      const result = await sessionManagementService.getExperienceDataMetrics(
        companyId,
        filter,
      );

      const points = Array.isArray(result.experienceDataPoints)
        ? result.experienceDataPoints
        : [];

      return points.map(toExperienceMetricPoint).map(toExperienceUsageData);
    },
    enabled: Boolean(companyId) && enabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  return {
    data: data ?? [],
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
